import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { compose } from '../../node_module_clones/recompose';
import { useSelector } from 'react-redux';

import SelectionMarquee from '../canvas/selection/SelectionMarquee';
import OrchestratedList from '../list/OrchestratedList';
import List from '../list/List';
import EmptyInboxPlaceholder from './EmptyInboxPlaceholder';

import markSeenInboxPointersDecorator from '../components/pointsOfInterest/pointers/markSeenInboxPointersDecorator';
import selectionMarqueeCanvasStateDecorator from '../canvas/selection/selectionMarqueeCanvasStateDecorator';
import renderListPlaceholdersDecorator from '../element/elementPlaceholder/renderListPlaceholdersDecorator';
import { zoomStateContextConsumer } from '../canvas/zoom/ZoomStateContext';
import useInboxReturnState from './returnState/useInboxReturnState';
import { isPlatformIpad } from '../platform/utils/platformDetailsUtils';
import { getPlatformDetailsSelector } from '../platform/platformSelector';

import inboxCanDrop from './inboxCanDrop';

import { ELEMENT_DEFAULT_WIDTH } from '../../common/elements/elementConstants';
import { IN_LIST_MODE } from '../../common/inList/listTypes';

import './InboxScrollableList.scss';

type InboxScrollableListProps = {
    gridSize: number;
    isEditable: boolean;
    isHovered: boolean;
    inboxElementIds: string[];
    currentBoardId: string;
    selectionMarqueeActive: boolean;
    setSelectionMarqueeActive: (active: boolean) => void;
    listElementProps: any;
    elementPlaceholderMeasurements: any;
    onElementVisibilityChange: Function;
    getInitialRenderSize: (defaultSize: number, listElementsIds: string[]) => number;
    getInitialElementsVisibility: (listElementsIds: string[]) => Map<string, boolean>;
    saveElementRenderState: () => void;
    clearPreviousElementRenderState: () => void;
    initialListRenderSize?: number;
    listStyle?: CSSStyleRule;
    getContextZoomScale: () => number;
};

const MARQUEE_OFFSET = { x: 0, y: -5 };
const INITIAL_RENDER_SIZE = 20;

const enhance = compose(
    markSeenInboxPointersDecorator,
    selectionMarqueeCanvasStateDecorator,
    renderListPlaceholdersDecorator,
    zoomStateContextConsumer,
);

const InboxScrollableList = (props: InboxScrollableListProps) => {
    const {
        gridSize,
        isEditable,
        inboxElementIds,
        currentBoardId,
        selectionMarqueeActive,
        setSelectionMarqueeActive,
        listElementProps,
        elementPlaceholderMeasurements,
        onElementVisibilityChange,
        getInitialRenderSize,
        getInitialElementsVisibility,

        saveElementRenderState,
        clearPreviousElementRenderState,
        initialListRenderSize,

        listStyle,
    } = props;

    const listContainerRef = useRef(null);

    const platformDetails = useSelector(getPlatformDetailsSelector);

    // We need to re-render when the ref updates due to ensure that the selection
    // marquee's list container ref is up to date and attaches events to the correct
    // DOM node
    const [listContainerNode, setListContainerNode] = useState(null);
    const listContainerRefCallback = useCallback((node) => {
        if (node !== null) {
            listContainerRef.current = node;
            setListContainerNode(node);
        }
    }, []);

    const cleanupOnOrchestrationEnd = useInboxReturnState(currentBoardId, listContainerRef, platformDetails);

    const batchedRenderInitialSize =
        initialListRenderSize || getInitialRenderSize(INITIAL_RENDER_SIZE, inboxElementIds);
    const initialElementsVisibility = getInitialElementsVisibility(inboxElementIds);

    // on unmount, save the orchestrated list element render state
    useEffect(() => {
        return () => {
            saveElementRenderState();
        };
    }, []);

    // on board change, clear the previous element render state
    useEffect(() => {
        clearPreviousElementRenderState();
    }, [currentBoardId]);

    return (
        <div
            className={classNames('InboxScrollableList', {
                'selection-marquee-active': selectionMarqueeActive,
            })}
            data-scrollable
        >
            <SelectionMarquee
                sectionElementIds={inboxElementIds}
                setSelectionMarqueeActive={setSelectionMarqueeActive}
                eventListenerNode={listContainerNode}
                offset={MARQUEE_OFFSET}
                containerWidth={gridSize * (ELEMENT_DEFAULT_WIDTH + 3.9)}
                gridSize={gridSize}
                disabled={!isEditable}
            />
            <OrchestratedList
                key={currentBoardId}
                childElementIds={inboxElementIds}
                onOrchestrationEnd={cleanupOnOrchestrationEnd}
                initialRenderSize={batchedRenderInitialSize}
            >
                {(childElementIds: string[], listSize: number) => {
                    return (
                        <List
                            {...props}
                            childElementIds={childElementIds}
                            listSize={listSize}
                            listId={currentBoardId}
                            listContainerRef={listContainerRefCallback}
                            placeholderComponent={<EmptyInboxPlaceholder {...props} />}
                            inListClass={IN_LIST_MODE.IN_LIST_UNSORTED_NOTES}
                            listCanDropFn={inboxCanDrop}
                            listElementProps={{
                                ...listElementProps,
                                onElementVisibilityChange,
                                initialElementsVisibility,
                                elementPlaceholderMeasurements,
                            }}
                            style={listStyle}
                        />
                    );
                }}
            </OrchestratedList>
            {isPlatformIpad(platformDetails) && <div className="ipad-virtual-keyboard-toolbar-inset"></div>}
        </div>
    );
};

// @ts-ignore-next-line The props are correct, just confused by untyped decorators
export default enhance(InboxScrollableList);
